/**
 * Kind Patches GLP landing page — surprise-pack gift guard
 * --------------------------------------------------------
 * Keeps ONE cart line at quantity 1 on every page: the $0 surprise pack that
 * the GLP buy box adds (config emitted by snippets/kp-gift-guard.liquid).
 *
 * Two jobs, both scoped to that single variant id:
 *   1. Clamp: after any cart change, read the cart and, when that line sits
 *      above 1, set it back to 1 through /cart/change.js; when the cart no
 *      longer holds the parent product at the gift tier's quantity, remove
 *      it (a $0 item must not survive an emptied cart).
 *   2. Lock: hide the quantity controls on that line in the UpCart drawer and
 *      on the native cart page (CSS in the snippet, attributes stamped here).
 *
 * It never adds a line, never touches another variant, removes only the gift
 * line itself (parent quantity below the minimum) and exits early when the
 * variant is not in the cart. Deliberately independent
 * from assets/gwp.js (Avenue Z's threshold gift), which only knows its own
 * product; the structural drawer matching below is the same approach that
 * file and shipping-protection.js already use.
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('kp-gift-guard-config');
  if (!cfgEl) return;
  var config;
  try { config = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  var VARIANT_ID = String(config.variantId || '');
  if (!VARIANT_ID) return;
  var PARENT_ID = String(config.parentProductId || '');
  var PARENT_MIN = Number(config.parentMinQty) || 0;

  var changeUrl = config.changeUrl || '/cart/change';
  if (changeUrl.indexOf('.js') === -1) changeUrl += '.js';
  var cartUrl = (config.cartUrl || '/cart').replace(/\/$/, '') + '.js';
  var OWN_HEADER = 'X-KP-Gift-Guard';

  // ---------- Clamp ----------

  var busy = false;
  var pending = false;
  var queued = 0;

  function findLine(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === VARIANT_ID) return items[i];
    }
    return null;
  }

  function parentQty(cart) {
    var items = (cart && cart.items) || [];
    var n = 0;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].product_id) === PARENT_ID) n += Number(items[i].quantity) || 0;
    }
    return n;
  }

  function clamp() {
    if (busy) { pending = true; return; }
    busy = true;
    fetch(cartUrl, { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var line = findLine(cart);
        if (!line) return false;
        var want = 1;
        if (PARENT_ID && PARENT_MIN > 0 && parentQty(cart) < PARENT_MIN) want = 0;
        if (line.quantity === want) return false;
        var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
        headers[OWN_HEADER] = '1';
        return fetch(changeUrl, { method: 'POST', headers: headers, body: JSON.stringify({ id: line.key, quantity: want }) })
          .then(function (res) { return res.ok; });
      })
      .then(function (changed) {
        if (!changed) return;
        if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
        // The native cart page renders server-side; show the corrected line.
        if (document.querySelector('cart-items') && !document.querySelector('.upcart-public-cart-item')) window.location.reload();
      })
      .catch(function () {})
      .then(function () {
        busy = false;
        if (pending) { pending = false; scheduleClamp(); }
      });
  }

  function scheduleClamp() {
    clearTimeout(queued);
    queued = setTimeout(clamp, 250);
  }

  // ---------- Lock ----------

  var QTY_CONTROLS = 'input[type="number"], select, [class*="quantity" i], [class*="stepper" i], [aria-label*="quantity" i]';

  function decorate() {
    var rows = document.querySelectorAll('.upcart-public-cart-item');
    for (var i = 0; i < rows.length; i++) {
      var el = rows[i];
      if (el.hasAttribute('data-kp-gift-guard')) continue;
      var link = el.querySelector('a[href*="?variant="]');
      if (!link) continue;
      var id = ((link.getAttribute('href') || '').split('?variant=')[1] || '').split('&')[0];
      if (id !== VARIANT_ID) continue;
      el.setAttribute('data-kp-gift-guard', '');
      var controls = el.querySelectorAll(QTY_CONTROLS);
      for (var j = 0; j < controls.length; j++) controls[j].setAttribute('data-kp-gift-hide', '');
    }
    var native = document.querySelectorAll('.cart-item[data-id="' + VARIANT_ID + '"]');
    for (var k = 0; k < native.length; k++) native[k].setAttribute('data-kp-gift-guard', '');
  }

  var raf = 0;
  function scheduleDecorate() {
    if (raf) return;
    raf = requestAnimationFrame(function () { raf = 0; decorate(); });
  }

  // ---------- Triggers ----------

  function hookUpcart() {
    var tries = 0;
    (function attempt() {
      if (typeof window.upcartSubscribeCartUpdated === 'function') {
        window.upcartSubscribeCartUpdated(function () { scheduleDecorate(); scheduleClamp(); });
        if (typeof window.upcartSubscribeCartLoaded === 'function') window.upcartSubscribeCartLoaded(function () { scheduleDecorate(); scheduleClamp(); });
        if (typeof window.upcartSubscribeAddedToCart === 'function') window.upcartSubscribeAddedToCart(function () { scheduleDecorate(); scheduleClamp(); });
        return;
      }
      if (tries++ < 40) setTimeout(attempt, 500);
    })();
  }

  // Any cart mutation from any script (drawer, cart page, apps) triggers a
  // clamp; our own change call is tagged so it does not bounce off itself.
  function hookFetch() {
    if (typeof window.fetch !== 'function' || window.__kpGiftGuardFetch) return;
    window.__kpGiftGuardFetch = true;
    var orig = window.fetch.bind(window);
    window.fetch = function (resource, init) {
      var url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
      var own = !!(init && init.headers && (typeof init.headers.get === 'function' ? init.headers.get(OWN_HEADER) : init.headers[OWN_HEADER]));
      var p = orig(resource, init);
      if (!own && /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url)) {
        p.then(function (res) { if (res && res.ok) scheduleClamp(); }, function () {});
      }
      return p;
    };
  }

  function init() {
    decorate();
    if (document.body && 'MutationObserver' in window) {
      new MutationObserver(scheduleDecorate).observe(document.body, { childList: true, subtree: true });
    }
    hookUpcart();
    hookFetch();
    document.addEventListener('cart:updated', scheduleClamp);
    scheduleClamp();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
